import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Input, alert } from 'equasion-lib';
import {
  FiChevronLeft,
  FiX,
  FiCheckCircle,
  FiAlertTriangle,
  FiMapPin,
  FiChevronDown,
  FiChevronUp,
  FiPlus
} from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import { SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { useZoneScanLookup } from '@/lib/shared-coms/ZoneScanner';
import { buzz, playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import './PickPathSetup.scss';

type PathZone = {
  id: string;
  name: string;
  zoneType?: string;
  area?: string | null;
  rack?: string | null;
  shelf?: string | null;
  bin?: string | null;
  pickSequence?: number | null;
};

type PathLocation = {
  id: string;
  companyName?: string;
  address?: { lineOne?: string; city?: string; state?: string } | null;
};

const onPath = (zone: PathZone) => typeof zone.pickSequence === 'number';

/** "Area SP · Rack 10 · Shelf 01" — the physical spot, for zones whose names do not say. */
const zonePlace = (zone: PathZone) =>
  [
    zone.area && `Area ${zone.area}`,
    zone.rack && `Rack ${zone.rack}`,
    zone.shelf && `Shelf ${zone.shelf}`,
    zone.bin && `Bin ${zone.bin}`
  ]
    .filter(Boolean)
    .join(' · ');

const locationLabel = (location: PathLocation) => {
  const { address } = location;
  const cityState = [address?.city, address?.state].filter(Boolean).join(', ');
  return (
    [address?.lineOne, cityState].filter(Boolean).join(' — ') ||
    location.companyName ||
    'Shipping location'
  );
};

const sameWalk = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * One stop on the rail. `index` is the 0-based position; the node shows it 1-based, which is
 * what a person counting stops on the floor would say.
 */
function Stop({
  index,
  zone,
  latest,
  onRemove
}: {
  index: number;
  zone: PathZone;
  latest?: boolean;
  onRemove?: () => void;
}) {
  const place = zonePlace(zone);
  return (
    <li className={['pps-stop', latest && 'pps-stop--latest'].filter(Boolean).join(' ')}>
      <span className="pps-stop__node" aria-hidden>
        {index + 1}
      </span>
      <div className="pps-stop__body">
        <div className="pps-stop__name">
          {zone.name}
          {zone.zoneType === 'quarantine' && (
            <span className="pps-stop__tag">quarantine</span>
          )}
        </div>
        {!!place && <div className="pps-stop__meta">{place}</div>}
      </div>
      {onRemove && (
        <button
          type="button"
          className="pps-stop__remove"
          aria-label={`Remove ${zone.name} from the path`}
          onClick={onRemove}
        >
          <FiX />
        </button>
      )}
    </li>
  );
}

/** Type a zone name when its label will not scan; runs the same lookup as the camera. */
function ManualStop({ onAdd }: { onAdd: (text: string) => Promise<void> }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const text = value.trim();
    if (!text) return;
    setBusy(true);
    await onAdd(text);
    setBusy(false);
    setValue('');
  };
  return (
    <div className="pps-manual">
      <strong>Add a stop by name</strong>
      <p>
        For a zone whose label will not scan. It is added as the next stop, exactly as a
        scan would be.
      </p>
      <Input
        form={false}
        label="Zone name"
        value={value}
        onChange={({ value: next }) => setValue(next)}
      />
      <Button type="primary" fullWidth onClick={submit} disabled={busy || !value.trim()}>
        Add as next stop
      </Button>
    </div>
  );
}

function PickPathSetup({
  companyId,
  canEdit,
  locations,
  locationId,
  setLocationId,
  zones,
  zonesLoading,
  refetchZones,
  zonePickPathSet,
  saving
}: {
  companyId: string;
  canEdit: boolean;
  locations: PathLocation[];
  locationId: string | null;
  setLocationId: (id: string | null) => void;
  zones: PathZone[];
  zonesLoading: boolean;
  refetchZones: () => Promise<unknown>;
  zonePickPathSet: (opts: { variables: Record<string, unknown> }) => Promise<unknown>;
  saving: boolean;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'overview' | 'walking'>('overview');
  // The walk being captured: zone ids, first stop first. Only meaningful while walking.
  const [walk, setWalk] = useState<string[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [remainingOpen, setRemainingOpen] = useState(false);
  const railEndRef = useRef<HTMLLIElement>(null);
  const remainingRef = useRef<HTMLUListElement>(null);
  const lookupZoneCode = useZoneScanLookup({
    companyId,
    locationId: locationId || undefined
  });

  const zoneById = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);
  // The path as it is SAVED, in walk order.
  const savedPath = useMemo(
    () =>
      zones
        .filter(onPath)
        .sort(
          (a, b) =>
            (a.pickSequence as number) - (b.pickSequence as number) ||
            a.name.localeCompare(b.name)
        ),
    [zones]
  );
  const savedIds = useMemo(() => savedPath.map((z) => z.id), [savedPath]);
  const walkSet = useMemo(() => new Set(walk), [walk]);
  const remaining = useMemo(
    () =>
      zones
        .filter((z) => !walkSet.has(z.id))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [zones, walkSet]
  );
  const location = locations.find((l) => l.id === locationId) || null;
  const walkChanged = !sameWalk(walk, savedIds);

  // Keep the newest stop in view: the rail grows under the camera as the floor is walked.
  useEffect(() => {
    if (mode === 'walking') railEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [walk.length, mode]);

  useEffect(() => {
    if (mode === 'walking' && remainingOpen) {
      remainingRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [mode, remainingOpen]);

  const startWalk = (fromScratch: boolean) => {
    setWalk(fromScratch ? [] : savedIds);
    setRemainingOpen(false);
    setMode('walking');
  };

  const addStop = (zone: PathZone) => {
    const at = walk.indexOf(zone.id);
    // The label still in frame after a successful read is not a second stop.
    if (at === walk.length - 1 && at >= 0) return;
    if (at >= 0) {
      scanAlert(
        `Already stop ${at + 1}`,
        `${zone.name} is already on this walk. Remove it from the list first if it belongs here instead.`,
        5,
        'warning'
      );
      return;
    }
    buzz(40);
    playSuccessTone();
    setWalk((current) => [...current, zone.id]);
  };

  const handleScan = async (result) => {
    const code = (result?.text || '').trim();
    if (!code) return;
    if (result?.gtin || result?.sscc) {
      scanAlert(
        'That is a product barcode',
        'Scan the label on the zone itself — the walk is a list of zones, not products.',
        5,
        'warning'
      );
      return;
    }
    const { zone, matches, error } = await lookupZoneCode(code);
    if (error) {
      scanAlert(
        'Zone lookup failed',
        operatorFacingMessage(error, 'Could not look up that zone. Please try again.'),
        6,
        'error'
      );
      return;
    }
    if (!zone) {
      scanAlert(
        matches.length > 1 ? 'More than one zone matches' : 'Zone not found',
        matches.length > 1
          ? `“${code}” matches ${matches.length} zones here. Add it from the "not yet on the walk" list instead.`
          : `No zone at this location matches “${code}”.`,
        6,
        'error'
      );
      return;
    }
    // Resolve through the loaded floor so the stop carries area/rack/shelf/bin for the rail.
    addStop(zoneById.get(zone.id) || zone);
  };

  const removeStop = (zoneId: string) =>
    setWalk((current) => current.filter((id) => id !== zoneId));

  const leaveWalk = () => {
    if (walkChanged) setDiscardOpen(true);
    else setMode('overview');
  };

  const savePath = async () => {
    try {
      await zonePickPathSet({ variables: { companyId, locationId, zoneIds: walk } });
      await refetchZones();
      setSavedCount(walk.length);
    } catch (e) {
      alert(
        'Could not save the path',
        operatorFacingMessage(e, 'Please try again.'),
        6,
        'error'
      );
    }
  };

  // ---------------------------------------------------------------- location chooser
  if (!locationId) {
    return (
      <Page className="pick-path-setup action-page">
        <div className="action-top-bar">
          <Button
            type="secondary"
            iconBefore={<FiChevronLeft />}
            onClick={() => navigate('/')}
          >
            Back
          </Button>
          <div className="status">Pick Path</div>
        </div>
        <div className="action-page-body">
          <div className="pps-intro">
            <h1>Which floor?</h1>
            <p>A pick path is walked per shipping location.</p>
          </div>
          {locations.length === 0 && (
            <div className="pps-empty">
              <FiAlertTriangle />
              <p>
                This company has no shipping location yet, so there is no floor to walk.
              </p>
            </div>
          )}
          <ul className="pps-locations">
            {locations.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className="pps-location"
                  onClick={() => setLocationId(l.id)}
                >
                  <FiMapPin />
                  <span>{locationLabel(l)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Page>
    );
  }

  // ---------------------------------------------------------------- walking
  if (mode === 'walking') {
    return (
      <Page className="pick-path-setup pick-path-setup--walking action-page">
        <div className="action-top-bar">
          <Button type="secondary" iconBefore={<FiChevronLeft />} onClick={leaveWalk}>
            Back
          </Button>
          <div className="status">
            <span className="pps-status-dot" />
            Walking
          </div>
        </div>
        <div className="action-page-body">
          <div className="pps-camera">
            <SharedScanner
              scanProfile={SCAN_PROFILE.ZONE}
              handleScan={handleScan}
              replaceManualEntry
              alternativeManualEntry={
                <ManualStop onAdd={(text) => handleScan({ text })} />
              }
            />
          </div>

          <div className="pps-walk-scroll">
            <div className="pps-stat">
              <span className="pps-stat__number">{walk.length}</span>
              <span className="pps-stat__label">
                {walk.length === 1 ? 'stop' : 'stops'}
                <small>next is #{walk.length + 1}</small>
              </span>
            </div>
            <p className="pps-hint">
              Walk the floor the way you pick it and scan each zone label as you pass it.
              Aisle by aisle, the same direction every time.
            </p>

            <ol className="pps-rail">
              {walk.map((id, index) => {
                const zone = zoneById.get(id);
                if (!zone) return null;
                return (
                  <React.Fragment key={id}>
                    <Stop
                      index={index}
                      zone={zone}
                      latest={index === walk.length - 1}
                      onRemove={() => removeStop(id)}
                    />
                  </React.Fragment>
                );
              })}
              <li ref={railEndRef} aria-hidden className="pps-rail__end" />
            </ol>

            <button
              type="button"
              className="pps-remaining__toggle"
              onClick={() => setRemainingOpen((open) => !open)}
            >
              <span>
                {remaining.length} {remaining.length === 1 ? 'zone' : 'zones'} not yet on
                the walk
              </span>
              {remainingOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {remainingOpen && (
              <ul ref={remainingRef} className="pps-remaining">
                {remaining.length === 0 && (
                  <li className="pps-remaining__done">
                    <FiCheckCircle /> Every zone on this floor is on the walk.
                  </li>
                )}
                {remaining.map((zone) => (
                  <li key={zone.id}>
                    <button
                      type="button"
                      className="pps-remaining__zone"
                      onClick={() => addStop(zone)}
                    >
                      <span>
                        <span className="pps-remaining__name">{zone.name}</span>
                        {!!zonePlace(zone) && (
                          <span className="pps-remaining__place">{zonePlace(zone)}</span>
                        )}
                      </span>
                      <FiPlus />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pps-actions">
            <Button
              type="primary"
              fullWidth
              onClick={savePath}
              disabled={saving || !walkChanged}
            >
              {saving
                ? 'Saving…'
                : walk.length === 0
                ? savedIds.length
                  ? 'Save — clears the path'
                  : 'Nothing to save yet'
                : `Save path · ${walk.length} ${walk.length === 1 ? 'stop' : 'stops'}`}
            </Button>
          </div>
        </div>

        <div
          className={['result-panel', discardOpen && 'open'].filter(Boolean).join(' ')}
        >
          <div className="result-message">
            <FiAlertTriangle style={{ color: 'var(--orange)' }} />
            <h1>Leave without saving?</h1>
            <p>
              The stops you scanned on this walk will be lost. The saved path stays as it
              was.
            </p>
          </div>
          <div className="result-actions">
            <Button
              type="secondary"
              fullWidth
              onClick={() => {
                setDiscardOpen(false);
                setMode('overview');
              }}
            >
              Discard walk
            </Button>
            <Button type="primary" fullWidth onClick={() => setDiscardOpen(false)}>
              Keep walking
            </Button>
          </div>
        </div>

        <div
          className={['result-panel', savedCount !== null && 'open']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>Path saved</h1>
            <p>
              {savedCount === 0
                ? 'The pick path has been cleared. Pick lists go back to order-line order.'
                : `${savedCount} ${
                    savedCount === 1 ? 'stop' : 'stops'
                  }. From now on every pick list at ${
                    location ? locationLabel(location) : 'this location'
                  } is sorted in this order.`}
            </p>
          </div>
          <div className="result-actions">
            <Button type="secondary" fullWidth onClick={() => navigate('/sales-orders')}>
              Go to Sales Orders
            </Button>
            <Button
              type="primary"
              fullWidth
              onClick={() => {
                setSavedCount(null);
                setMode('overview');
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  // ---------------------------------------------------------------- overview
  const offPath = zones.length - savedPath.length;
  return (
    <Page className="pick-path-setup action-page">
      <div className="action-top-bar">
        <Button
          type="secondary"
          iconBefore={<FiChevronLeft />}
          onClick={() => (locations.length > 1 ? setLocationId(null) : navigate('/'))}
        >
          Back
        </Button>
        <div className="status">Pick Path</div>
      </div>
      <div className="action-page-body">
        {location && (
          <div className="pps-location-line">
            <FiMapPin />
            <span>{locationLabel(location)}</span>
          </div>
        )}

        <div className="pps-stat">
          <span className="pps-stat__number">
            {zonesLoading ? '…' : savedPath.length}
          </span>
          <span className="pps-stat__label">
            {savedPath.length === 1 ? 'stop' : 'stops'}
            {!zonesLoading && (
              <small>
                {offPath === 0
                  ? 'every zone is on the path'
                  : `${offPath} ${offPath === 1 ? 'zone' : 'zones'} not on the path`}
              </small>
            )}
          </span>
        </div>

        {!zonesLoading && savedPath.length === 0 && (
          <div className="pps-empty">
            <p>
              <strong>No pick path yet.</strong> Walk the floor once, scanning each zone
              label in the order you pass it. Every pick list is then sorted in that
              order, from wherever the picker starts.
            </p>
          </div>
        )}

        {canEdit ? (
          <div className="pps-actions pps-actions--top">
            <Button type="primary" fullWidth onClick={() => startWalk(true)}>
              {savedPath.length ? 'Re-walk from the start' : 'Walk the floor'}
            </Button>
            {savedPath.length > 0 && (
              <Button type="secondary" fullWidth onClick={() => startWalk(false)}>
                Continue after stop {savedPath.length}
              </Button>
            )}
          </div>
        ) : (
          <p className="pps-hint">
            Only a user who can update zones can walk the path. Ask an admin.
          </p>
        )}

        {savedPath.length > 0 && (
          <ol className="pps-rail pps-rail--saved">
            {savedPath.map((zone, index) => (
              <React.Fragment key={zone.id}>
                <Stop index={index} zone={zone} />
              </React.Fragment>
            ))}
          </ol>
        )}
      </div>
    </Page>
  );
}

export default PickPathSetup;
